import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useOutletContext } from 'react-router-dom';
import Icon from '../../components/dashboard/DashboardIcons';
import { API, auth, PageHead, fmtDate } from '../organization/finance/shared';

const VISIBILITY_LABELS = { private: 'خاص', organizations_only: 'ظاهر للمنشآت فقط', public_directory: 'ظاهر في الدليل العام' };
const AVAILABILITY_LABELS = { available: 'متاح', available_offers_only: 'متاح للعروض فقط', unavailable: 'غير متاح' };
const AVAILABILITY_COLORS = { available: 'bg-teal-50 text-teal-700', available_offers_only: 'bg-amber-50 text-amber-700', unavailable: 'bg-slate-100 text-slate-500' };

const QUICK_ACCESS = [
  { label: 'مكتبة التمارين', desc: 'استكشف وابتكر تمارين تدريبية', icon: 'field', path: '/coach/studio/exercises', testId: 'coach-quick-exercises' },
  { label: 'الخطط التدريبية', desc: 'خطط وتابع برامج فرقك وفئاتك', icon: 'clipboard', path: '/coach/studio/plans', testId: 'coach-quick-plans' },
  { label: 'الاستديو الفني', desc: 'لوحة العمل الفني والجلسات', icon: 'dumbbell', path: '/coach/studio', testId: 'coach-quick-studio' },
];

const QuickAccessCard = ({ item }) => (
  <Link
    to={item.path}
    data-testid={item.testId}
    className="group relative overflow-hidden rounded-2xl border border-slate-800/10 bg-gradient-to-br from-[#101b2e] to-[#16233b] p-5 shadow-[0_4px_16px_rgba(16,27,46,0.18)] transition-transform active:scale-[0.97] hover:shadow-[0_10px_28px_rgba(16,27,46,0.3)]"
  >
    <span className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 rounded-full bg-teal-400/15 blur-2xl transition-opacity group-hover:opacity-80" />
    <Icon name="chevronLeft" className="absolute left-4 top-5 h-4 w-4 text-slate-500 transition-transform group-hover:-translate-x-1 group-hover:text-teal-300" />
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 text-[#0c1420] shadow-sm">
      <Icon name={item.icon} className="h-5 w-5" strokeWidth={2} />
    </span>
    <div className="mt-3 text-sm font-bold text-white">{item.label}</div>
    <div className="mt-1 text-xs leading-relaxed text-slate-400">{item.desc}</div>
  </Link>
);

const StatCard = ({ icon, label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-transform active:scale-[0.98]" data-testid={testId}>
    <div className="flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 text-teal-600"><Icon name={icon} className="w-4 h-4" /></span>
      <span className="text-xs font-semibold text-slate-400">{label}</span>
    </div>
    <div className="mt-2.5 text-2xl font-black text-slate-900">{value}</div>
  </div>
);

export default function CoachDashboardPage() {
  const { profile } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/coaches/me/dashboard`, auth())
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse text-sm text-slate-400" data-testid="coach-dashboard-loading">جارِ التحميل...</div>;

  return (
    <div className="space-y-6" data-testid="coach-dashboard-page">
      <PageHead title={`أهلاً، ${profile?.full_name_ar || 'المدرب'}`} desc="نظرة سريعة على ملفك وارتباطاتك وطلباتك" />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="coach-quick-access-section">
        {QUICK_ACCESS.map((item) => <QuickAccessCard key={item.testId} item={item} />)}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-completion-card">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-400"><span>اكتمال الملف المهني</span><span>{data?.profile_completion_percent ?? 0}%</span></div>
          <div className="mt-2.5 h-2 rounded-full bg-slate-100">
            <div className="h-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500" style={{ width: `${data?.profile_completion_percent ?? 0}%` }} />
          </div>
          {(data?.profile_completion_percent ?? 0) < 100 && (
            <Link to="/coach/profile" className="mt-3 inline-block text-xs font-bold text-teal-600 active:opacity-70">إكمال الملف</Link>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-visibility-card">
          <div className="text-xs font-semibold text-slate-400">حالة الظهور</div>
          <div className="mt-2 text-sm font-bold text-slate-800">{VISIBILITY_LABELS[data?.profile_visibility] || '—'}</div>
          <span className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-bold ${AVAILABILITY_COLORS[data?.availability_status] || 'bg-slate-100 text-slate-500'}`}>
            {AVAILABILITY_LABELS[data?.availability_status] || '—'}
          </span>
        </div>
        <StatCard icon="building" label="منشآت نشطة" value={data?.active_memberships_count ?? 0} testId="coach-stat-memberships" />
        <StatCard icon="inbox" label="طلبات وعروض جديدة" value={(data?.pending_requests_count ?? 0) + (data?.pending_offers_count ?? 0)} testId="coach-stat-requests" />
      </div>

      {data?.expiring_certifications?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-expiring-certs-alert">
          <div className="flex items-center gap-2 text-amber-800 font-bold text-sm"><Icon name="clock" className="w-4 h-4" /> شهادات تقترب من الانتهاء</div>
          <ul className="mt-2 space-y-1 text-xs font-semibold text-amber-700">
            {data.expiring_certifications.map((c) => <li key={c.id}>{c.name} — ينتهي في {fmtDate(c.expiry_date)}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5" data-testid="coach-active-orgs-card">
          <h3 className="text-sm font-bold text-slate-800 mb-3">المنشآت المرتبط بها</h3>
          {(!data?.active_memberships || data.active_memberships.length === 0) ? (
            <p className="text-sm text-slate-400">لا يوجد ارتباط نشط حالياً</p>
          ) : (
            <ul className="space-y-2">
              {data.active_memberships.map((m) => (
                <li key={m.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm">
                  <span className="font-semibold text-slate-700">{m.club_name}</span>
                  <span className="text-xs font-medium text-slate-400">{m.title}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/coach/organizations" className="mt-3 inline-block text-xs font-bold text-teal-600 active:opacity-70">عرض الكل</Link>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5" data-testid="coach-upcoming-matches-card">
          <h3 className="text-sm font-bold text-slate-800 mb-3">المباريات القادمة</h3>
          {(!data?.upcoming_matches || data.upcoming_matches.length === 0) ? (
            <p className="text-sm text-slate-400">لا توجد مباريات قادمة لفرقك المسندة</p>
          ) : (
            <ul className="space-y-2">
              {data.upcoming_matches.map((m) => (
                <li key={m.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm">
                  <span className="font-semibold text-slate-700">{m.club_name}</span>
                  <span className="text-xs font-medium text-slate-400">{fmtDate(m.match_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
